import { BlockTool, BlockToolData } from '@editorjs/editorjs'
import { UploaderService } from '../../../shared/service/uploader.service'
import { UploadImageResponse } from '../../../shared/type/upload'
import { resolvePathFile } from '../../../../core/until'

interface TwoColumnLayoutData extends BlockToolData {
  imageUrl: string
  imageAlt: string
  textContent: string
  layout: 'image-left' | 'image-right' | 'image-both'
  imageWidth: number
  textAlignment: 'left' | 'center' | 'right'
  uploadedFilename?: string // Store the uploaded filename from backend
  // Second image properties for dual image layout
  secondImageUrl?: string
  secondImageAlt?: string
  secondUploadedFilename?: string
}

interface TwoColumnLayoutConfig {
  placeholder?: string
  defaultLayout?: 'image-left' | 'image-right' | 'image-both'
}

export class TwoColumnLayoutTool implements BlockTool {
  private data: TwoColumnLayoutData
  private config: TwoColumnLayoutConfig
  private api: any
  private uploaderService: UploaderService
  private wrapper!: HTMLElement
  private imageInput!: HTMLInputElement
  private imageUploadInput!: HTMLInputElement
  private imageAltInput!: HTMLInputElement
  private richTextContainer!: HTMLDivElement
  private imageWidthInput!: HTMLInputElement
  private textAlignmentSelect!: HTMLSelectElement
  // Second image elements
  private secondImageInput!: HTMLInputElement
  private secondImageUploadInput!: HTMLInputElement
  private secondImageAltInput!: HTMLInputElement

  constructor({ data, config, api }: {
    data: TwoColumnLayoutData
    config: TwoColumnLayoutConfig
    api: any
  }) {
    // Ensure data has all required properties with defaults
    this.data = {
      imageUrl: data?.imageUrl || '',
      imageAlt: data?.imageAlt || '',
      textContent: data?.textContent || '',
      layout: data?.layout || (config?.defaultLayout || 'image-left'),
      imageWidth: data?.imageWidth || 40,
      textAlignment: data?.textAlignment || 'left',
      uploadedFilename: data?.uploadedFilename || '',
      secondImageUrl: data?.secondImageUrl || '',
      secondImageAlt: data?.secondImageAlt || '',
      secondUploadedFilename: data?.secondUploadedFilename || ''
    }
    this.config = config || {}
    this.api = api
    this.uploaderService = new UploaderService()
    
    console.log('TwoColumnLayoutTool constructor called with:', { 
      originalData: data, 
      processedData: this.data, 
      config: this.config, 
      api: this.api 
    })
  }

  static get toolbox() {
    return {
      title: 'Two Column Layout',
      icon: '<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M2 2h16v16H2V2zm2 2v12h4V4H4zm6 0v12h8V4h-8z"/></svg>'
    }
  }

  static get isReadOnlySupported() {
    return true
  }

  render() {
    console.log('TwoColumnLayoutTool render called, api.readOnly:', this.api.readOnly)
    console.log('TwoColumnLayoutTool current data:', this.data)
    
    // Check if we're in read-only mode by checking if the editor is read-only
    const isReadOnly = this.api.readOnly && typeof this.api.readOnly === 'boolean' ? this.api.readOnly : false
    
    if (isReadOnly) {
      console.log('Rendering in read-only mode')
      return this.renderReadOnly()
    }

    // Otherwise render the editing interface
    console.log('Rendering in edit mode')
    return this.renderEditMode()
  }

  private renderReadOnly(): HTMLElement {
    const wrapper = document.createElement('div')
    wrapper.classList.add('two-column-layout-display')
    wrapper.style.cssText = `
      margin: 16px 0;
      padding: 0;
    `

    const imageWidth = this.data.imageWidth || 40
    const textWidth = 100 - imageWidth
    const layout = this.data.layout || 'image-left'
    const textAlign = this.data.textAlignment || 'left'

    let contentHTML = ''

    if (layout === 'image-left') {
      contentHTML = `
        <div style="display: flex; gap: 16px; align-items: start; flex-wrap: wrap;">
          <div style="width: ${imageWidth}%; min-width: 200px;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: unset;">` : 
              '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Image</div>'
            }
          </div>
          <div style="width: ${textWidth}%; min-width: 250px; text-align: ${textAlign};">
            <div style="padding: 20px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
              ${this.data.textContent ? 
                `<div style="margin: 0; line-height: 1.7; color: #333; font-size: 16px;">${this.data.textContent}</div>` : 
                '<div style="margin: 0; color: #999; font-style: italic;">No content</div>'
              }
            </div>
          </div>
        </div>
      `
    } else if (layout === 'image-right') {
      contentHTML = `
        <div style="display: flex; gap: 16px; align-items: start; flex-wrap: wrap;">
          <div style="width: ${textWidth}%; min-width: 250px; text-align: ${textAlign};">
            <div style="padding: 20px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
              ${this.data.textContent ? 
                `<div style="margin: 0; line-height: 1.7; color: #333; font-size: 16px;">${this.data.textContent}</div>` : 
                '<div style="margin: 0; color: #999; font-style: italic;">No content</div>'
              }
            </div>
          </div>
          <div style="width: ${imageWidth}%; min-width: 200px;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: unset;">` : 
              '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Image</div>'
            }
          </div>
        </div>
      `
    } else if (layout === 'image-both') {
      contentHTML = `
        <div style="display: flex; gap: 16px; align-items: start; flex-wrap: wrap;">
          <div style="width: ${imageWidth}%; min-width: 200px;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: unset;">` : 
              '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Image</div>'
            }
          </div>
          <div style="width: ${textWidth}%; min-width: 200px;">
            ${this.data.secondImageUrl ? 
              `<img src="${this.data.secondImageUrl}" alt="${this.data.secondImageAlt || 'Second Image'}" style="width: 100%; height: auto; border-radius: 8px; object-fit: cover; box-shadow: unset;">` : 
              '<div style="width: 100%; height: 200px; background: #f5f5f5; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ddd;">No Second Image</div>'
            }
          </div>
        </div>
      `
    }

    wrapper.innerHTML = contentHTML
    return wrapper
  }

  private renderEditMode(): HTMLElement {
    this.wrapper = document.createElement('div')
    this.wrapper.classList.add('two-column-layout-tool')
    this.wrapper.style.cssText = `
      border: 1px solid #ddd;
      border-radius: 8px;
      padding: 16px;
      background: #f9f9f9;
      margin: 8px 0;
    `

    // Layout selection with toggle buttons
    const layoutContainer = document.createElement('div')
    layoutContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const layoutLabel = document.createElement('label')
    layoutLabel.textContent = 'Layout:'
    layoutLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    const buttonContainer = document.createElement('div')
    buttonContainer.style.cssText = `
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    `

    // Create toggle buttons for each layout
    const layouts = [
      {
        value: 'image-left',
        icon: `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="8" height="16" fill="#4A90E2" rx="2"/>
          <rect x="12" y="2" width="6" height="16" fill="#E8F4FD" rx="2"/>
          <text x="15" y="12" text-anchor="middle" font-size="8" fill="#666">T</text>
        </svg>`,
        title: 'Image Left, Text Right'
      },
      {
        value: 'image-right',
        icon: `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="6" height="16" fill="#E8F4FD" rx="2"/>
          <rect x="10" y="2" width="8" height="16" fill="#4A90E2" rx="2"/>
          <text x="5" y="12" text-anchor="middle" font-size="8" fill="#666">T</text>
        </svg>`,
        title: 'Text Left, Image Right'
      },
      {
        value: 'image-both',
        icon: `<svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="2" width="8" height="16" fill="#4A90E2" rx="2"/>
          <rect x="12" y="2" width="6" height="16" fill="#4A90E2" rx="2"/>
        </svg>`,
        title: 'Image Left, Image Right'
      }
    ]

    const currentLayout = this.data.layout || this.config.defaultLayout || 'image-left'

    // Function to update button styles
    const updateButtonStyles = (selectedLayout: string) => {
      const allButtons = buttonContainer.querySelectorAll('button[data-layout]') as NodeListOf<HTMLButtonElement>
      allButtons.forEach(btn => {
        const layoutValue = btn.getAttribute('data-layout')
        if (layoutValue === selectedLayout) {
          btn.style.borderColor = '#4A90E2'
          btn.style.background = '#E8F4FD'
        } else {
          btn.style.borderColor = '#ddd'
          btn.style.background = 'white'
        }
      })
    }

    layouts.forEach(layout => {
      const button = document.createElement('button')
      button.type = 'button'
      button.title = layout.title
      button.innerHTML = layout.icon
      button.setAttribute('data-layout', layout.value)
      
      // Set initial style
      const isSelected = currentLayout === layout.value
      button.style.cssText = `
        padding: 12px;
        border: 2px solid ${isSelected ? '#4A90E2' : '#ddd'};
        background: ${isSelected ? '#E8F4FD' : 'white'};
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 48px;
        min-height: 48px;
      `

      // Add hover effects
      button.addEventListener('mouseenter', () => {
        if (this.data.layout !== layout.value) {
          button.style.borderColor = '#4A90E2'
          button.style.background = '#F0F8FF'
        }
      })

      button.addEventListener('mouseleave', () => {
        if (this.data.layout !== layout.value) {
          button.style.borderColor = '#ddd'
          button.style.background = 'white'
        }
      })

      button.addEventListener('click', () => {
        // Update data first
        this.data.layout = layout.value as 'image-left' | 'image-right' | 'image-both'
        
        // Update all button styles
        updateButtonStyles(layout.value)
        
        // Trigger UI updates
        this.toggleSecondImageVisibility()
        this.updatePreview()
      })

      buttonContainer.appendChild(button)
    })

    layoutContainer.appendChild(layoutLabel)
    layoutContainer.appendChild(buttonContainer)

    // Image input section
    const imageContainer = document.createElement('div')
    imageContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const imageLabel = document.createElement('label')
    imageLabel.textContent = 'Image:'
    imageLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    // Create tabs for URL vs Upload
    const tabContainer = document.createElement('div')
    tabContainer.style.cssText = `
      display: flex;
      margin-bottom: 8px;
      border-bottom: 1px solid #ddd;
    `

    const urlTab = document.createElement('button')
    urlTab.textContent = 'URL'
    urlTab.type = 'button'
    urlTab.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #f0f0f0;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      font-size: 14px;
    `

    const uploadTab = document.createElement('button')
    uploadTab.textContent = 'Upload'
    uploadTab.type = 'button'
    uploadTab.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #f0f0f0;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      font-size: 14px;
    `

    // URL input
    this.imageInput = document.createElement('input')
    this.imageInput.type = 'url'
    this.imageInput.placeholder = 'https://example.com/image.jpg'
    this.imageInput.value = this.data.imageUrl || ''
    this.imageInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      display: block;
    `
    this.imageInput.addEventListener('input', () => {
      this.data.imageUrl = this.imageInput.value
      this.updatePreview()
    })

    // File upload container
    const uploadContainer = document.createElement('div')
    uploadContainer.style.cssText = `
      display: none;
      width: 100%;
    `

    // File upload input
    this.imageUploadInput = document.createElement('input')
    this.imageUploadInput.type = 'file'
    this.imageUploadInput.accept = 'image/*'
    this.imageUploadInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      margin-bottom: 8px;
    `
    this.imageUploadInput.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        this.handleFileUpload(file)
      }
    })

    // Drag and drop area
    const dropArea = document.createElement('div')
    dropArea.id = 'drop-area'
    dropArea.style.cssText = `
      width: 100%;
      height: 120px;
      border: 2px dashed #ccc;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
    `
    dropArea.innerHTML = `
      <div id="drop-icon" style="font-size: 24px; color: #999; margin-bottom: 8px;">📁</div>
      <div id="drop-text" style="color: #666; font-size: 14px;">Drag & drop image here</div>
      <div id="drop-subtext" style="color: #999; font-size: 12px; margin-top: 4px;">or click to browse</div>
      <div id="upload-progress" style="display: none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center;">
        <div style="font-size: 24px; color: #1890ff; margin-bottom: 8px;">⏳</div>
        <div style="color: #1890ff; font-size: 14px;">Uploading...</div>
      </div>
    `

    // Handle drag and drop
    dropArea.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropArea.style.borderColor = '#1890ff'
      dropArea.style.background = '#f0f8ff'
    })

    dropArea.addEventListener('dragleave', (e) => {
      e.preventDefault()
      dropArea.style.borderColor = '#ccc'
      dropArea.style.background = '#fafafa'
    })

    dropArea.addEventListener('drop', (e) => {
      e.preventDefault()
      dropArea.style.borderColor = '#ccc'
      dropArea.style.background = '#fafafa'
      
      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        const file = files[0]
        if (file.type.startsWith('image/')) {
          this.handleFileUpload(file)
        }
      }
    })

    dropArea.addEventListener('click', () => {
      this.imageUploadInput.click()
    })

    uploadContainer.appendChild(this.imageUploadInput)
    uploadContainer.appendChild(dropArea)

    // Tab switching logic
    const showUrlInput = () => {
      this.imageInput.style.display = 'block'
      uploadContainer.style.display = 'none'
      urlTab.style.borderBottomColor = '#1890ff'
      urlTab.style.background = 'white'
      uploadTab.style.borderBottomColor = 'transparent'
      uploadTab.style.background = '#f0f0f0'
    }

    const showUploadInput = () => {
      this.imageInput.style.display = 'none'
      uploadContainer.style.display = 'block'
      uploadTab.style.borderBottomColor = '#1890ff'
      uploadTab.style.background = 'white'
      urlTab.style.borderBottomColor = 'transparent'
      urlTab.style.background = '#f0f0f0'
    }

    urlTab.addEventListener('click', showUrlInput)
    uploadTab.addEventListener('click', showUploadInput)

    // Initialize with URL tab active
    showUrlInput()

    tabContainer.appendChild(urlTab)
    tabContainer.appendChild(uploadTab)

    imageContainer.appendChild(imageLabel)
    imageContainer.appendChild(tabContainer)
    imageContainer.appendChild(this.imageInput)
    imageContainer.appendChild(uploadContainer)

    // Image Alt Text input
    const imageAltContainer = document.createElement('div')
    imageAltContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const imageAltLabel = document.createElement('label')
    imageAltLabel.textContent = 'Image Alt Text:'
    imageAltLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    this.imageAltInput = document.createElement('input')
    this.imageAltInput.type = 'text'
    this.imageAltInput.placeholder = 'Description of the image'
    this.imageAltInput.value = this.data.imageAlt || ''
    this.imageAltInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
    `
    this.imageAltInput.addEventListener('input', () => {
      this.data.imageAlt = this.imageAltInput.value
    })

    imageAltContainer.appendChild(imageAltLabel)
    imageAltContainer.appendChild(this.imageAltInput)

    // Second Image input section (initially hidden)
    const secondImageContainer = document.createElement('div')
    secondImageContainer.id = 'second-image-container'
    secondImageContainer.style.cssText = `
      margin-bottom: 16px;
      display: none;
    `

    const secondImageLabel = document.createElement('label')
    secondImageLabel.textContent = 'Second Image:'
    secondImageLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    // Create tabs for second image URL vs Upload
    const secondTabContainer = document.createElement('div')
    secondTabContainer.style.cssText = `
      display: flex;
      margin-bottom: 8px;
      border-bottom: 1px solid #ddd;
    `

    const secondUrlTab = document.createElement('button')
    secondUrlTab.textContent = 'URL'
    secondUrlTab.type = 'button'
    secondUrlTab.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #f0f0f0;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      font-size: 14px;
    `

    const secondUploadTab = document.createElement('button')
    secondUploadTab.textContent = 'Upload'
    secondUploadTab.type = 'button'
    secondUploadTab.style.cssText = `
      padding: 8px 16px;
      border: none;
      background: #f0f0f0;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      font-size: 14px;
    `

    // Second image URL input
    this.secondImageInput = document.createElement('input')
    this.secondImageInput.type = 'url'
    this.secondImageInput.placeholder = 'https://example.com/image2.jpg'
    this.secondImageInput.value = this.data.secondImageUrl || ''
    this.secondImageInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      display: block;
    `
    this.secondImageInput.addEventListener('input', () => {
      this.data.secondImageUrl = this.secondImageInput.value
      this.updatePreview()
    })

    // Second image file upload container
    const secondUploadContainer = document.createElement('div')
    secondUploadContainer.style.cssText = `
      display: none;
      width: 100%;
    `

    // Second image file upload input
    this.secondImageUploadInput = document.createElement('input')
    this.secondImageUploadInput.type = 'file'
    this.secondImageUploadInput.accept = 'image/*'
    this.secondImageUploadInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
      margin-bottom: 8px;
    `
    this.secondImageUploadInput.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        this.handleSecondFileUpload(file)
      }
    })

    // Second image drag and drop area
    const secondDropArea = document.createElement('div')
    secondDropArea.id = 'second-drop-area'
    secondDropArea.style.cssText = `
      width: 100%;
      height: 120px;
      border: 2px dashed #ccc;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #fafafa;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
    `
    secondDropArea.innerHTML = `
      <div id="second-drop-icon" style="font-size: 24px; color: #999; margin-bottom: 8px;">📁</div>
      <div id="second-drop-text" style="color: #666; font-size: 14px;">Drag & drop second image here</div>
      <div id="second-drop-subtext" style="color: #999; font-size: 12px; margin-top: 4px;">or click to browse</div>
      <div id="second-upload-progress" style="display: none; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center;">
        <div style="font-size: 24px; color: #1890ff; margin-bottom: 8px;">⏳</div>
        <div style="color: #1890ff; font-size: 14px;">Uploading...</div>
      </div>
    `

    // Handle second image drag and drop
    secondDropArea.addEventListener('dragover', (e) => {
      e.preventDefault()
      secondDropArea.style.borderColor = '#1890ff'
      secondDropArea.style.background = '#f0f8ff'
    })

    secondDropArea.addEventListener('dragleave', (e) => {
      e.preventDefault()
      secondDropArea.style.borderColor = '#ccc'
      secondDropArea.style.background = '#fafafa'
    })

    secondDropArea.addEventListener('drop', (e) => {
      e.preventDefault()
      secondDropArea.style.borderColor = '#ccc'
      secondDropArea.style.background = '#fafafa'
      
      const files = e.dataTransfer?.files
      if (files && files.length > 0) {
        const file = files[0]
        if (file.type.startsWith('image/')) {
          this.handleSecondFileUpload(file)
        }
      }
    })

    secondDropArea.addEventListener('click', () => {
      this.secondImageUploadInput.click()
    })

    secondUploadContainer.appendChild(this.secondImageUploadInput)
    secondUploadContainer.appendChild(secondDropArea)

    // Second image tab switching logic
    const showSecondUrlInput = () => {
      this.secondImageInput.style.display = 'block'
      secondUploadContainer.style.display = 'none'
      secondUrlTab.style.borderBottomColor = '#1890ff'
      secondUrlTab.style.background = 'white'
      secondUploadTab.style.borderBottomColor = 'transparent'
      secondUploadTab.style.background = '#f0f0f0'
    }

    const showSecondUploadInput = () => {
      this.secondImageInput.style.display = 'none'
      secondUploadContainer.style.display = 'block'
      secondUploadTab.style.borderBottomColor = '#1890ff'
      secondUploadTab.style.background = 'white'
      secondUrlTab.style.borderBottomColor = 'transparent'
      secondUrlTab.style.background = '#f0f0f0'
    }

    secondUrlTab.addEventListener('click', showSecondUrlInput)
    secondUploadTab.addEventListener('click', showSecondUploadInput)

    // Initialize with URL tab active
    showSecondUrlInput()

    secondTabContainer.appendChild(secondUrlTab)
    secondTabContainer.appendChild(secondUploadTab)

    secondImageContainer.appendChild(secondImageLabel)
    secondImageContainer.appendChild(secondTabContainer)
    secondImageContainer.appendChild(this.secondImageInput)
    secondImageContainer.appendChild(secondUploadContainer)

    // Second Image Alt Text input
    const secondImageAltContainer = document.createElement('div')
    secondImageAltContainer.id = 'second-image-alt-container'
    secondImageAltContainer.style.cssText = `
      margin-bottom: 16px;
      display: none;
    `

    const secondImageAltLabel = document.createElement('label')
    secondImageAltLabel.textContent = 'Second Image Alt Text:'
    secondImageAltLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    this.secondImageAltInput = document.createElement('input')
    this.secondImageAltInput.type = 'text'
    this.secondImageAltInput.placeholder = 'Description of the second image'
    this.secondImageAltInput.value = this.data.secondImageAlt || ''
    this.secondImageAltInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
    `
    this.secondImageAltInput.addEventListener('input', () => {
      this.data.secondImageAlt = this.secondImageAltInput.value
    })

    secondImageAltContainer.appendChild(secondImageAltLabel)
    secondImageAltContainer.appendChild(this.secondImageAltInput)

    // Image width input
    const imageWidthContainer = document.createElement('div')
    imageWidthContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const imageWidthLabel = document.createElement('label')
    imageWidthLabel.textContent = 'Image Width (%):'
    imageWidthLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    this.imageWidthInput = document.createElement('input')
    this.imageWidthInput.type = 'number'
    this.imageWidthInput.min = '20'
    this.imageWidthInput.max = '80'
    this.imageWidthInput.step = '5'
    this.imageWidthInput.placeholder = '40'
    this.imageWidthInput.value = String(this.data.imageWidth || 40)
    this.imageWidthInput.style.cssText = `
      width: 100%;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
    `
    this.imageWidthInput.addEventListener('input', () => {
      this.data.imageWidth = parseInt(this.imageWidthInput.value) || 40
      this.updatePreview()
    })

    imageWidthContainer.appendChild(imageWidthLabel)
    imageWidthContainer.appendChild(this.imageWidthInput)

    // Rich Text Editor
    const textContainer = document.createElement('div')
    textContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const textLabel = document.createElement('label')
    textLabel.textContent = 'Text Content (Rich Text):'
    textLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    // Rich text toolbar
    const toolbar = document.createElement('div')
    toolbar.style.cssText = `
      display: flex;
      gap: 4px;
      margin-bottom: 8px;
      flex-wrap: wrap;
    `

    const toolbarButtons = [
      { text: 'B', title: 'Bold', style: 'font-weight: bold;' },
      { text: 'I', title: 'Italic', style: 'font-style: italic;' },
      { text: 'U', title: 'Underline', style: 'text-decoration: underline;' },
      { text: '🔗', title: 'Insert Link', style: 'font-size: 16px;' },
      { text: '📝', title: 'Clear Formatting', style: 'font-size: 14px;' }
    ]

    toolbarButtons.forEach(btn => {
      const button = document.createElement('button')
      button.textContent = btn.text
      button.title = btn.title
      button.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ccc;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        min-width: 32px;
        ${btn.style}
      `
      
      button.addEventListener('click', (e) => {
        e.preventDefault()
        this.handleToolbarAction(btn.title.toLowerCase())
      })
      
      button.addEventListener('mouseenter', () => {
        button.style.backgroundColor = '#f0f0f0'
      })
      
      button.addEventListener('mouseleave', () => {
        button.style.backgroundColor = 'white'
      })
      
      toolbar.appendChild(button)
    })

    // Rich text content area
    this.richTextContainer = document.createElement('div')
    this.richTextContainer.contentEditable = 'true'
    this.richTextContainer.style.cssText = `
      width: 100%;
      min-height: 120px;
      padding: 12px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: white;
      font-size: 14px;
      line-height: 1.5;
      overflow-y: auto;
      outline: none;
    `
    
    // Set initial content
    if (this.data.textContent) {
      this.richTextContainer.innerHTML = this.data.textContent
    } else {
      this.richTextContainer.innerHTML = `<p>${this.config.placeholder || 'Enter your rich text content here...'}</p>`
    }

    // Handle content changes
    this.richTextContainer.addEventListener('input', () => {
      this.data.textContent = this.richTextContainer.innerHTML
      this.updatePreview()
    })

    // Handle paste to clean up HTML
    this.richTextContainer.addEventListener('paste', (e) => {
      e.preventDefault()
      const text = e.clipboardData?.getData('text/html') || e.clipboardData?.getData('text/plain') || ''
      document.execCommand('insertHTML', false, text)
    })

    textContainer.appendChild(textLabel)
    textContainer.appendChild(toolbar)
    textContainer.appendChild(this.richTextContainer)

    // Text alignment input
    const textAlignContainer = document.createElement('div')
    textAlignContainer.style.cssText = `
      margin-bottom: 16px;
    `

    const textAlignLabel = document.createElement('label')
    textAlignLabel.textContent = 'Text Alignment:'
    textAlignLabel.style.cssText = `
      display: block;
      font-weight: bold;
      margin-bottom: 8px;
    `

    this.textAlignmentSelect = document.createElement('select')
    this.textAlignmentSelect.value = this.data.textAlignment || 'left'
    this.textAlignmentSelect.style.cssText = `
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 14px;
    `
    this.textAlignmentSelect.innerHTML = `
      <option value="left">Left</option>
      <option value="center">Center</option>
      <option value="right">Right</option>
    `
    this.textAlignmentSelect.addEventListener('change', () => {
      this.data.textAlignment = this.textAlignmentSelect.value as 'left' | 'center' | 'right'
      this.updatePreview()
    })

    textAlignContainer.appendChild(textAlignLabel)
    textAlignContainer.appendChild(this.textAlignmentSelect)

    // Preview section
    const preview = document.createElement('div')
    preview.style.cssText = `
      margin-top: 16px;
      padding: 16px;
      background: white;
      border-radius: 4px;
      border: 1px solid #eee;
    `
    preview.innerHTML = '<h4>Preview:</h4>'
    
    const previewContent = document.createElement('div')
    previewContent.id = 'two-column-preview'
    previewContent.style.cssText = `
      margin-top: 8px;
    `
    preview.appendChild(previewContent)

    // Append all elements
    this.wrapper.appendChild(layoutContainer)
    this.wrapper.appendChild(imageContainer)
    this.wrapper.appendChild(imageAltContainer)
    this.wrapper.appendChild(secondImageContainer)
    this.wrapper.appendChild(secondImageAltContainer)
    this.wrapper.appendChild(imageWidthContainer)
    this.wrapper.appendChild(textContainer)
    this.wrapper.appendChild(textAlignContainer)
    this.wrapper.appendChild(preview)

    // Initial preview update and visibility setup
    this.toggleSecondImageVisibility()
    this.updatePreview()

    return this.wrapper
  }

  private showUploadProgress(show: boolean) {
    const dropArea = this.wrapper.querySelector('#drop-area') as HTMLElement
    const dropIcon = this.wrapper.querySelector('#drop-icon') as HTMLElement
    const dropText = this.wrapper.querySelector('#drop-text') as HTMLElement
    const dropSubtext = this.wrapper.querySelector('#drop-subtext') as HTMLElement
    const uploadProgress = this.wrapper.querySelector('#upload-progress') as HTMLElement

    if (show) {
      dropIcon.style.display = 'none'
      dropText.style.display = 'none'
      dropSubtext.style.display = 'none'
      uploadProgress.style.display = 'block'
      dropArea.style.cursor = 'not-allowed'
      dropArea.style.opacity = '0.7'
    } else {
      dropIcon.style.display = 'block'
      dropText.style.display = 'block'
      dropSubtext.style.display = 'block'
      uploadProgress.style.display = 'none'
      dropArea.style.cursor = 'pointer'
      dropArea.style.opacity = '1'
    }
  }

  private showSecondUploadProgress(show: boolean) {
    const dropArea = this.wrapper.querySelector('#second-drop-area') as HTMLElement
    const dropIcon = this.wrapper.querySelector('#second-drop-icon') as HTMLElement
    const dropText = this.wrapper.querySelector('#second-drop-text') as HTMLElement
    const dropSubtext = this.wrapper.querySelector('#second-drop-subtext') as HTMLElement
    const uploadProgress = this.wrapper.querySelector('#second-upload-progress') as HTMLElement

    if (show) {
      dropIcon.style.display = 'none'
      dropText.style.display = 'none'
      dropSubtext.style.display = 'none'
      uploadProgress.style.display = 'block'
      dropArea.style.cursor = 'not-allowed'
      dropArea.style.opacity = '0.7'
    } else {
      dropIcon.style.display = 'block'
      dropText.style.display = 'block'
      dropSubtext.style.display = 'block'
      uploadProgress.style.display = 'none'
      dropArea.style.cursor = 'pointer'
      dropArea.style.opacity = '1'
    }
  }

  private toggleSecondImageVisibility() {
    const secondImageContainer = this.wrapper.querySelector('#second-image-container') as HTMLElement
    const secondImageAltContainer = this.wrapper.querySelector('#second-image-alt-container') as HTMLElement
    
    // Find text containers by looking for specific labels
    const textContainer = Array.from(this.wrapper.querySelectorAll('div')).find(div => {
      const label = div.querySelector('label')
      return label && label.textContent?.includes('Text Content')
    }) as HTMLElement
    
    const textAlignContainer = Array.from(this.wrapper.querySelectorAll('div')).find(div => {
      const label = div.querySelector('label')
      return label && label.textContent?.includes('Text Alignment')
    }) as HTMLElement

    if (this.data.layout === 'image-both') {
      if (secondImageContainer) secondImageContainer.style.display = 'block'
      if (secondImageAltContainer) secondImageAltContainer.style.display = 'block'
      // Hide text-related containers
      if (textContainer) textContainer.style.display = 'none'
      if (textAlignContainer) textAlignContainer.style.display = 'none'
    } else {
      if (secondImageContainer) secondImageContainer.style.display = 'none'
      if (secondImageAltContainer) secondImageAltContainer.style.display = 'none'
      // Show text-related containers
      if (textContainer) textContainer.style.display = 'block'
      if (textAlignContainer) textAlignContainer.style.display = 'block'
    }
  }

  private async handleFileUpload(file: File) {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.')
      return
    }

    try {
      // Show loading state
      this.showUploadProgress(true)

      // Create object URL for immediate preview
      const objectURL = URL.createObjectURL(file)
      this.data.imageUrl = objectURL
      this.imageInput.value = objectURL
      this.updatePreview()

      // Upload to backend
      const uploadResponse = await this.uploaderService.uploadImages([file as any])
      
      if (uploadResponse && uploadResponse.length > 0) {
        const uploadedFile = uploadResponse[0]
        this.data.uploadedFilename = resolvePathFile(uploadedFile.filename)
        
        // Update image URL to use the backend URL
        // You might want to construct the full URL based on your backend configuration
        this.data.imageUrl = resolvePathFile(uploadedFile.filename)
        this.imageInput.value = this.data.imageUrl
        this.updatePreview()
        
        console.log('Image uploaded successfully:', uploadedFile)
      }
    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Failed to upload image. Please try again.')
      
      // Revert to object URL if upload fails
      const objectURL = URL.createObjectURL(file)
      this.data.imageUrl = objectURL
      this.imageInput.value = objectURL
      this.updatePreview()
    } finally {
      // Hide loading state
      this.showUploadProgress(false)
    }
  }

  private async handleSecondFileUpload(file: File) {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB.')
      return
    }

    try {
      // Show loading state
      this.showSecondUploadProgress(true)

      // Create object URL for immediate preview
      const objectURL = URL.createObjectURL(file)
      this.data.secondImageUrl = objectURL
      this.secondImageInput.value = objectURL
      this.updatePreview()

      // Upload to backend
      const uploadResponse = await this.uploaderService.uploadImages([file as any])
      
      if (uploadResponse && uploadResponse.length > 0) {
        const uploadedFile = uploadResponse[0]
        this.data.secondUploadedFilename = resolvePathFile(uploadedFile.filename)
        
        // Update image URL to use the backend URL
        this.data.secondImageUrl = resolvePathFile(uploadedFile.filename)
        this.secondImageInput.value = this.data.secondImageUrl
        this.updatePreview()
        
        console.log('Second image uploaded successfully:', uploadedFile)
      }
    } catch (error) {
      console.error('Error uploading second image:', error)
      alert('Failed to upload second image. Please try again.')
      
      // Revert to object URL if upload fails
      const objectURL = URL.createObjectURL(file)
      this.data.secondImageUrl = objectURL
      this.secondImageInput.value = objectURL
      this.updatePreview()
    } finally {
      // Hide loading state
      this.showSecondUploadProgress(false)
    }
  }

  private handleToolbarAction(action: string) {
    switch (action) {
      case 'bold':
        document.execCommand('bold', false)
        break
      case 'italic':
        document.execCommand('italic', false)
        break
      case 'underline':
        document.execCommand('underline', false)
        break
      case 'insert link':
        const url = prompt('Enter URL:')
        if (url) {
          document.execCommand('createLink', false, url)
        }
        break
      case 'clear formatting':
        document.execCommand('removeFormat', false)
        break
    }
    
    // Update data after formatting
    this.data.textContent = this.richTextContainer.innerHTML
    this.updatePreview()
    
    // Focus back to editor
    this.richTextContainer.focus()
  }

  private updatePreview() {
    const previewContent = this.wrapper.querySelector('#two-column-preview')
    if (!previewContent) return

    const imageWidth = this.data.imageWidth || 40
    const textWidth = 100 - imageWidth

    const layout = this.data.layout || 'image-left'
    const textAlign = this.data.textAlignment || 'left'

    let previewHTML = ''

    if (layout === 'image-left') {
      previewHTML = `
        <div style="display: flex; gap: 16px; align-items: start;">
          <div style="width: ${imageWidth}%;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 4px; object-fit: cover;">` : 
              '<div style="width: 100%; height: 200px; background: #eee; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ccc;">No Image</div>'
            }
          </div>
          <div style="width: ${textWidth}%; text-align: ${textAlign};">
            <div style="padding: 16px; background: transparent; border-radius: 4px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
              ${this.data.textContent ? 
                `<div style="margin: 0; line-height: 1.6; color: #333;">${this.data.textContent}</div>` : 
                '<div style="margin: 0; color: #999; font-style: italic;">Enter text content...</div>'
              }
            </div>
          </div>
        </div>
      `
    } else if (layout === 'image-right') {
      previewHTML = `
        <div style="display: flex; gap: 16px; align-items: start;">
          <div style="width: ${textWidth}%; text-align: ${textAlign};">
            <div style="padding: 16px; background: transparent; border-radius: 4px; min-height: 200px; display: flex; align-items: center; justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};">
              ${this.data.textContent ? 
                `<div style="margin: 0; line-height: 1.6; color: #333;">${this.data.textContent}</div>` : 
                '<div style="margin: 0; color: #999; font-style: italic;">Enter text content...</div>'
              }
            </div>
          </div>
          <div style="width: ${imageWidth}%;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 4px; object-fit: cover;">` : 
              '<div style="width: 100%; height: 200px; background: #eee; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ccc;">No Image</div>'
            }
          </div>
        </div>
      `
    } else if (layout === 'image-both') {
      previewHTML = `
        <div style="display: flex; gap: 16px; align-items: start;">
          <div style="width: ${imageWidth}%;">
            ${this.data.imageUrl ? 
              `<img src="${this.data.imageUrl}" alt="${this.data.imageAlt || 'Image'}" style="width: 100%; height: auto; border-radius: 4px; object-fit: cover;">` : 
              '<div style="width: 100%; height: 200px; background: #eee; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ccc;">No Image</div>'
            }
          </div>
          <div style="width: ${textWidth}%;">
            ${this.data.secondImageUrl ? 
              `<img src="${this.data.secondImageUrl}" alt="${this.data.secondImageAlt || 'Second Image'}" style="width: 100%; height: auto; border-radius: 4px; object-fit: cover;">` : 
              '<div style="width: 100%; height: 200px; background: #eee; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; border: 2px dashed #ccc;">No Second Image</div>'
            }
          </div>
        </div>
      `
    }

    previewContent.innerHTML = previewHTML
  }

  save() {
    return {
      imageUrl: this.data.imageUrl || '',
      imageAlt: this.data.imageAlt || '',
      textContent: this.data.textContent || '',
      layout: this.data.layout || 'image-left',
      imageWidth: this.data.imageWidth || 40,
      textAlignment: this.data.textAlignment || 'left',
      uploadedFilename: this.data.uploadedFilename || '',
      secondImageUrl: this.data.secondImageUrl || '',
      secondImageAlt: this.data.secondImageAlt || '',
      secondUploadedFilename: this.data.secondUploadedFilename || ''
    }
  }

  validate(savedData: TwoColumnLayoutData) {
    // Allow empty content during initial creation
    // Only validate that the data structure is correct
    if (typeof savedData !== 'object' || savedData === null) {
      return false
    }
    
    // Check if required fields exist (even if empty)
    if (typeof savedData.imageUrl !== 'string' || 
        typeof savedData.imageAlt !== 'string' || 
        typeof savedData.textContent !== 'string' ||
        typeof savedData.layout !== 'string' ||
        typeof savedData.imageWidth !== 'number' ||
        typeof savedData.textAlignment !== 'string') {
      return false
    }
    
    // uploadedFilename is optional
    if (savedData.uploadedFilename !== undefined && typeof savedData.uploadedFilename !== 'string') {
      return false
    }
    
    // Second image properties are optional
    if (savedData.secondImageUrl !== undefined && typeof savedData.secondImageUrl !== 'string') {
      return false
    }
    if (savedData.secondImageAlt !== undefined && typeof savedData.secondImageAlt !== 'string') {
      return false
    }
    if (savedData.secondUploadedFilename !== undefined && typeof savedData.secondUploadedFilename !== 'string') {
      return false
    }
    
    return true
  }

  static get pasteConfig() {
    return {
      tags: ['DIV', 'IMG'],
      files: {
        mimeTypes: ['image/*'],
        extensions: ['gif', 'jpg', 'png', 'svg', 'webp']
      }
    }
  }

  onPaste(event: CustomEvent) {
    const { data } = event.detail

    if (data.type === 'tag') {
      if (data.data.tag === 'IMG') {
        // Handle pasted image
        const img = data.data
        this.data.imageUrl = img.src || ''
        this.data.imageAlt = img.alt || ''
        this.imageInput.value = this.data.imageUrl
        this.imageAltInput.value = this.data.imageAlt
        this.updatePreview()
      } else if (data.data.tag === 'DIV') {
        // Handle pasted text content
        const div = data.data
        const text = div.textContent || ''
        if (text.length > 0 && text.length < 1000) {
          this.data.textContent = text
          this.richTextContainer.innerHTML = text
          this.updatePreview()
        }
      }
    }

    if (data.type === 'file' && data.data.type.startsWith('image/')) {
      // Handle pasted image file
      const file = data.data
      this.handleFileUpload(file)
      
      // Switch to upload tab to show the file was uploaded
      const uploadTab = this.wrapper.querySelector('button[type="button"]:last-of-type') as HTMLButtonElement
      if (uploadTab) {
        uploadTab.click()
      }
    }
  }
}

